import { Injectable } from '@angular/core';

import hl7FieldKeyMapRaw from '../../../assets/rule-config/field-key-map-hl7.json';
import {
  expandHl7ComponentKeyToSlashNotation,
  flattenHl7FieldKeyMapDocument,
  type Hl7FieldMapMetaValue,
  type Hl7FlattenedFieldMapValue,
  type Hl7ObrObxLocateMeta,
} from '../hl7/hl7-field-key-map.flatten';
import {
  parseHl7ObxParentIdPersistedPath,
  parseHl7ObrRepeatPersistedPath,
} from '../hl7/hl7-path-step-builder';
import {
  hl7FieldOptionDisplayLabel,
  type Hl7FieldMapMeta,
  type Hl7FieldOption,
} from '../models/field-option.model';

@Injectable()
export class Hl7FieldCatalogService {
  private loaded = false;
  private options: Hl7FieldOption[] = [];
  private flattenedFieldMap: Record<string, Hl7FieldMapMeta> = {};
  private cachedObrFilterKeys: string[] | null = null;
  private cachedObxFilterKeys: string[] | null = null;

  /** Load flattened HL7 field list and segment options once per session. */
  ensureLoaded(): void {
    if (this.loaded) {
      return;
    }
    const flat = flattenHl7FieldKeyMapDocument(hl7FieldKeyMapRaw);
    this.flattenedFieldMap = this.mapFlattenedToMeta(flat);
    this.cachedObrFilterKeys = null;
    this.cachedObxFilterKeys = null;

    const collected: Hl7FieldOption[] = [];
    const root = hl7FieldKeyMapRaw as Record<string, unknown>;
    this.collectFieldOptionsFromMapTree(root, collected, [], true);

    const byPath = new Map<string, Hl7FieldOption>();
    for (const o of collected) {
      if (!byPath.has(o.path)) {
        byPath.set(o.path, o);
      }
    }
    this.options = Array.from(byPath.values()).sort((a, b) =>
      hl7FieldOptionDisplayLabel(a).localeCompare(hl7FieldOptionDisplayLabel(b))
    );
    this.loaded = true;
  }

  getOptions(): Hl7FieldOption[] {
    this.ensureLoaded();
    return this.options;
  }

  getSegmentOptions(): Hl7FieldOption[] {
    this.ensureLoaded();
    return this.options.filter((o) => this.isBareHl7SegmentLabel(o.label));
  }

  getSegmentMatches(value: string): Hl7FieldOption[] {
    this.ensureLoaded();
    const q = this.normalizeSearch(value);
    const segments = this.getSegmentOptions();
    if (!q) {
      return segments;
    }
    return segments.filter((o) => this.normalizeSearch(o.label).includes(q));
  }

  getMetaByKey(key: string): Hl7FieldMapMeta | null {
    this.ensureLoaded();
    const k = (key ?? '').toString().trim();
    return this.flattenedFieldMap[k] ?? null;
  }

  getFlattenedMeta(displayKey: string): Hl7FieldMapMeta | null {
    this.ensureLoaded();
    const k = (displayKey ?? '').toString().trim();
    if (!k) {
      return null;
    }
    return this.flattenedFieldMap[k] ?? null;
  }

  findByKey(key: string): Hl7FieldOption | undefined {
    this.ensureLoaded();
    const k = (key ?? '').toString().trim();
    if (!k) {
      return undefined;
    }
    const meta = this.flattenedFieldMap[k];
    if (meta?.path) {
      return this.optionFromMeta(k, meta);
    }
    return this.options.find((o) => o.key === k);
  }

  findByLabel(label: string): Hl7FieldOption | undefined {
    this.ensureLoaded();
    const k = (label ?? '').toString().trim();
    if (!k) {
      return undefined;
    }
    const byKey = this.findByKey(k);
    if (byKey) {
      return byKey;
    }
    return this.options.find((o) => o.label === k || o.key === k);
  }

  findByPath(path: string): Hl7FieldOption | undefined {
    this.ensureLoaded();
    const p = this.trimFieldPath(path);
    return this.options.find((o) => this.trimFieldPath(o.path) === p);
  }

  findByPersistedPath(persisted: string): Hl7FieldOption | undefined {
    const norm = this.trimFieldPath(persisted);
    if (!norm) {
      return undefined;
    }
    this.ensureLoaded();
    const exact = this.options.find((o) => this.trimFieldPath(o.path) === norm);
    if (exact) {
      return exact;
    }
    const flatKey = this.findFlattenedKeyByPersistedPath(norm);
    if (flatKey) {
      return this.findByKey(flatKey);
    }
    return undefined;
  }

  findFlattenedKeyByPersistedPath(persisted: string): string | null {
    this.ensureLoaded();
    const norm = this.trimFieldPath(persisted);
    if (!norm) {
      return null;
    }
    for (const [key, meta] of Object.entries(this.flattenedFieldMap)) {
      if (this.trimFieldPath(meta.path) === norm) {
        return key;
      }
    }
    if (norm.includes('[@parentId')) {
      const parsed = parseHl7ObxParentIdPersistedPath(norm);
      if (parsed?.leaf) {
        return this.findFlattenedKeyByLeafTail(parsed.leaf);
      }
    }
    const obrRep = parseHl7ObrRepeatPersistedPath(norm);
    if (obrRep?.leaf) {
      return this.findFlattenedKeyByLeafTail(obrRep.leaf);
    }
    return null;
  }

  getCachedObrFilterKeys(): string[] {
    this.ensureLoaded();
    if (this.cachedObrFilterKeys) {
      return this.cachedObrFilterKeys;
    }
    for (const meta of Object.values(this.flattenedFieldMap)) {
      const keys = meta.hl7ObrObxLocate?.obrFieldKeys;
      if (keys?.length) {
        this.cachedObrFilterKeys = [...keys];
        return this.cachedObrFilterKeys;
      }
    }
    this.cachedObrFilterKeys = [];
    return this.cachedObrFilterKeys;
  }

  getCachedObxFilterKeys(): string[] {
    this.ensureLoaded();
    if (this.cachedObxFilterKeys) {
      return this.cachedObxFilterKeys;
    }
    for (const meta of Object.values(this.flattenedFieldMap)) {
      const keys = meta.hl7ObrObxLocate?.obxFieldKeys;
      if (keys?.length) {
        this.cachedObxFilterKeys = [...keys];
        return this.cachedObxFilterKeys;
      }
    }
    this.cachedObxFilterKeys = [];
    return this.cachedObxFilterKeys;
  }

  resolveRepeatLocate(segment: string, displayKey: string): Hl7ObrObxLocateMeta | null {
    this.ensureLoaded();
    const fromFlat = this.getFlattenedMeta(displayKey)?.hl7ObrObxLocate;
    if (fromFlat?.obrFieldKeys?.length) {
      return fromFlat;
    }
    const segUpper = (segment || '').trim().toUpperCase();
    const segOpt = this.options.find((o) => o.label.toUpperCase() === segUpper);
    if (!segOpt?.isRepeatable) {
      return null;
    }
    const obrKeys = this.getCachedObrFilterKeys();
    if (!obrKeys.length && segUpper !== 'OBX') {
      return null;
    }
    const obxKeys = segUpper === 'OBX' ? this.getCachedObxFilterKeys() : [];
    return {
      obrFieldKeys: obrKeys,
      obxFieldKeys: obxKeys,
      leafTail: expandHl7ComponentKeyToSlashNotation(displayKey),
    };
  }

  isBareHl7SegmentLabel(label: string): boolean {
    return /^[A-Za-z][A-Za-z0-9]{1,3}$/.test((label || '').trim());
  }

  isSegmentRepeatable(segmentOrKey: string): boolean {
    const raw = (segmentOrKey || '').trim();
    if (!raw) {
      return false;
    }
    const seg = raw.includes('.') ? raw.split('.')[0].toUpperCase() : raw.toUpperCase();
    const opt = this.options.find((o) => o.label.toUpperCase() === seg);
    return !!opt?.isRepeatable;
  }

  trimFieldPath(path: string): string {
    return (path || '').toString().trim().replace(/^\/+/, '');
  }

  private findFlattenedKeyByLeafTail(tail: string): string | null {
    const t = this.trimFieldPath(tail);
    if (!t) {
      return null;
    }
    for (const [key, meta] of Object.entries(this.flattenedFieldMap)) {
      const locate = meta.hl7ObrObxLocate;
      if (!locate) {
        continue;
      }
      const expanded = expandHl7ComponentKeyToSlashNotation((locate.leafTail || '').trim());
      if (t === expanded || t === (locate.leafTail || '').trim() || t.endsWith(`/${key}`)) {
        return key;
      }
    }
    return null;
  }

  private optionFromMeta(key: string, meta: Hl7FieldMapMeta): Hl7FieldOption {
    const path = (meta.path ?? '').toString().trim();
    return {
      key,
      label: key,
      path,
      leaf: this.leafFromPath(path),
      isRepeatable: !!meta.isRepeatable,
      hl7ObrObxLocate: meta.hl7ObrObxLocate,
    };
  }

  private mapFlattenedToMeta(
    flat: Record<string, Hl7FlattenedFieldMapValue>
  ): Record<string, Hl7FieldMapMeta> {
    const out: Record<string, Hl7FieldMapMeta> = {};
    for (const [k, v] of Object.entries(flat || {})) {
      if (!v) {
        continue;
      }
      if (typeof v === 'string') {
        out[k] = { path: this.trimFieldPath(v) };
        continue;
      }
      if (typeof v !== 'object' || !v.path) {
        continue;
      }
      const raw = v as Hl7FieldMapMetaValue;
      out[k] = {
        path: raw.path,
        isRepeatable: raw.isRepeatable,
        repeatableNodes: raw.repeatableNodes,
        hl7ObrObxLocate: raw.hl7ObrObxLocate,
      };
    }
    return out;
  }

  /**
   * Walk field-key-map JSON (HL7): collect top-level segment nodes under the message root (MSH, PID, …).
   */
  private collectFieldOptionsFromMapTree(
    node: Record<string, unknown>,
    out: Hl7FieldOption[],
    repeatableStack: string[],
    hl7SegmentMode: boolean
  ): void {
    if (!node || typeof node !== 'object') {
      return;
    }
    if (hl7SegmentMode && repeatableStack.length === 0) {
      const children = (node as { children?: unknown[] }).children;
      if (Array.isArray(children)) {
        for (const raw of children) {
          const ch = raw as {
            name?: string;
            label?: string;
            xpath?: string;
            isRepeatable?: boolean;
            children?: unknown[];
          };
          const name = (ch.name ?? '').toString().trim();
          const label = (ch.label ?? ch.name ?? '').toString().trim();
          let path = this.trimFieldPath((ch.xpath ?? '').toString());
          if (!path && name) {
            path = `HL7Message/${name}`;
          }
          if (!label || !name) {
            continue;
          }
          out.push({
            key: label,
            path,
            leaf: name,
            label,
            isRepeatable: !!ch.isRepeatable,
          });
          if (name === 'OBR' && Array.isArray(ch.children)) {
            const obxRaw = ch.children.find(
              (c) => ((c as { name?: string }).name ?? '').toString().trim() === 'OBX'
            ) as { name?: string; label?: string; xpath?: string; isRepeatable?: boolean } | undefined;
            if (obxRaw) {
              const obxName = (obxRaw.name ?? 'OBX').toString().trim();
              const obxLabel = (obxRaw.label ?? obxName).toString().trim();
              let obxPath = this.trimFieldPath((obxRaw.xpath ?? '').toString());
              if (!obxPath) {
                obxPath = 'HL7Message/OBR/OBX';
              }
              out.push({
                key: obxLabel,
                path: obxPath,
                leaf: obxName,
                label: obxLabel,
                isRepeatable: !!obxRaw.isRepeatable,
              });
            }
          }
        }
      }
      return;
    }

    const nodeName = ((node as { name?: string }).name ?? '').toString().trim();
    const isRepeatable = !!(node as { isRepeatable?: boolean }).isRepeatable;
    const stackHere =
      isRepeatable && nodeName ? [...repeatableStack, nodeName] : repeatableStack;

    const attrs = (node as { attributes?: unknown[] }).attributes;
    if (Array.isArray(attrs)) {
      for (const raw of attrs) {
        const attr = raw as { label?: string; xpath?: string; name?: string };
        const label = (attr.label ?? '').toString().trim();
        const path = (attr.xpath ?? '').toString().trim();
        if (!label || !path) {
          continue;
        }
        const repeatableNodes = stackHere.length ? [...stackHere] : undefined;
        out.push({
          key: label,
          path,
          leaf: this.leafFromPath(path),
          label,
          isRepeatable: !!repeatableNodes?.length,
        });
      }
    }
    const children = (node as { children?: unknown[] }).children;
    if (Array.isArray(children)) {
      for (const ch of children) {
        this.collectFieldOptionsFromMapTree(ch as Record<string, unknown>, out, stackHere, false);
      }
    }
    const flatFields = (node as { flatFields?: unknown[] }).flatFields;
    if (Array.isArray(flatFields)) {
      for (const raw of flatFields) {
        const ff = raw as { label?: string; xpath?: string };
        const label = (ff.label ?? '').toString().trim();
        const path = (ff.xpath ?? '').toString().trim();
        if (!label || !path) {
          continue;
        }
        out.push({
          key: label,
          path,
          leaf: this.leafFromPath(path),
          label,
          isRepeatable: false,
        });
      }
    }
  }

  private leafFromPath(path: string): string {
    const parts = (path ?? '').split('/').filter(Boolean);
    return parts.length ? parts[parts.length - 1] : path;
  }

  private normalizeSearch(input: string): string {
    return (input ?? '')
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }
}
